/**
 * Cached per-client face match threshold for offline-first verification.
 *
 * The authoritative value lives server-side in
 * ta_client_platform_config.platform_settings.face_match_threshold (system-admin
 * only). Devices cache the server-resolved value in the attendance meta store so
 * verifyFaceOffline can apply the same floor while offline.
 *
 * Refreshed by:
 * - offline-status snapshot downloads (masterSync)
 * - FaceScanIdentificationModal's platform-config fetch when online
 * - attendance_config_updated WebSocket events (SmartClock)
 *
 * Client-safe — no server imports.
 */
import attendanceStore from './attendanceStore';
import { clampFaceMatchThreshold } from '../faceMatchThresholds';

/**
 * Returns the cached server-resolved threshold, or null when no value has been
 * synced yet (callers should fall back to their built-in defaults).
 */
export async function getCachedFaceMatchThreshold(clientId: number): Promise<number | null> {
  if (!clientId) return null;
  try {
    const value = await attendanceStore.getMeta<unknown>(clientId, 'face_match_threshold');
    if (value === null || value === undefined) return null;
    const parsed = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(parsed) ? clampFaceMatchThreshold(parsed) : null;
  } catch (error) {
    console.warn('[FaceThresholdCache] Failed to read cached threshold:', error);
    return null;
  }
}

/**
 * Persist the server-resolved threshold. Pass null to clear (e.g. when the
 * admin resets the override — the next sync re-seeds the default).
 */
export async function setCachedFaceMatchThreshold(
  clientId: number,
  threshold: number | null,
): Promise<void> {
  if (!clientId) return;
  try {
    if (threshold === null || threshold === undefined || !Number.isFinite(threshold)) {
      await attendanceStore.deleteMeta(clientId, 'face_match_threshold');
      return;
    }
    await attendanceStore.setMeta(clientId, 'face_match_threshold', clampFaceMatchThreshold(threshold));
  } catch (error) {
    console.warn('[FaceThresholdCache] Failed to cache threshold:', error);
  }
}
